import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import Boton from './Boton'
import Modal from './Modal'
import BuscadorCliente from './BuscadorCliente'
import ModalAltaCliente from './ModalAltaCliente'
import { fmtFecha, fmtHoraDe, fmtMonto } from '../utils/fecha'
import { colors, font, text, space, radius, sx } from '../design/tokens'

/**
 * Tomar el carrito de un cliente (Martin, 15 oct 2026: "con algunos clientes
 * necesitamos poder entrar a su carrito y terminarles el pedido"). La tienda le
 * muestra al cliente un CÓDIGO en el carrito; acá el vendedor lo busca (o por
 * email/nombre si el cliente estaba logueado), elige la ficha y LGR arma la NV
 * en borrador con la misma plata que el checkout. La ficha nunca la elige el
 * sistema.
 */
export default function TomarCarrito() {
  const [q, setQ] = useState('')
  const [resultados, setResultados] = useState(null)
  const [buscando, setBuscando] = useState(false)
  const [elegido, setElegido] = useState(null)   // el carrito a tomar (abre el modal)
  const timer = useRef(null)

  const buscar = (texto) => {
    setQ(texto)
    clearTimeout(timer.current)
    if (texto.trim().length < 3) { setResultados(null); return }
    timer.current = setTimeout(async () => {
      setBuscando(true)
      try {
        const { data } = await api.get('/carritos-web/buscar', { params: { q: texto.trim() } })
        setResultados(data.carritos || [])
      } catch { setResultados([]) } finally { setBuscando(false) }
    }, 300)
  }

  return (
    <div style={st.card}>
      <div style={st.fila}>
        <strong style={st.titulo}>🛒 Terminarle el pedido a un cliente</strong>
        <input
          style={st.input}
          value={q}
          onChange={e => buscar(e.target.value)}
          placeholder="Código del carrito o link compartido (ligier.com.ar/c/…), email o nombre"
        />
        {buscando && <span style={st.meta}>Buscando…</span>}
      </div>
      <div style={st.ayuda}>
        El cliente ve su código en el carrito de ligier.com.ar, debajo del botón «Finalizar compra». Con el código, «Tomar carrito» arma la nota de venta con esos productos y los mismos precios de la tienda; después completás la entrega y le mandás el link de pago. Si el cliente te mandó un carrito compartido, pegá el link o su código.
      </div>

      {resultados && resultados.length === 0 && !buscando && (
        <div style={{ ...st.meta, marginTop: space.md }}>No hay ningún carrito con productos para «{q}». Pedile al cliente el código que le muestra el carrito.</div>
      )}
      {resultados && resultados.length > 0 && (
        <table style={st.tabla}>
          <thead>
            <tr>{['Código', 'Último cambio', 'Cliente', 'Productos', 'Subtotal', 'Acción'].map(h => <th key={h} style={{ ...st.th, ...(h === 'Subtotal' ? { textAlign: 'right' } : {}) }}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {resultados.map(c => (
              <tr key={`${c.tipo}-${c.id}`} style={st.tr}>
                <td style={{ ...st.td, ...sx.mono, fontWeight: 600 }}>{c.codigo || '—'}</td>
                <td style={{ ...st.td, ...sx.mono, color: colors.textSec, whiteSpace: 'nowrap' }}>{fmtFecha(c.visto_ultima_vez)} {fmtHoraDe(c.visto_ultima_vez)}</td>
                <td style={st.td}>{c.tipo === 'compartido'
                  ? <span style={{ color: colors.textMeta }}>carrito compartido</span>
                  : (c.nombre || c.email || <span style={{ color: colors.textMeta }}>anónimo</span>)}</td>
                <td style={{ ...st.td, color: colors.textSec, maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={(c.lineas || []).map(l => `${l.cantidad}× ${l.nombre || l.sku}`).join('\n')}>
                  {(c.lineas || []).map(l => `${l.cantidad}× ${l.nombre || l.sku}`).join(' · ')}
                </td>
                <td style={{ ...st.td, ...sx.mono, textAlign: 'right', whiteSpace: 'nowrap' }} title={c.motivo || undefined}>{c.total != null ? `$${fmtMonto(c.total)}` : '—'}</td>
                <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                  <AccionCarrito carrito={c} onTomar={() => setElegido(c)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {elegido && <ModalTomar carrito={elegido} onClose={() => setElegido(null)} />}
    </div>
  )
}

/** Una sola acción por fila: tomar, o ir a la NV que ya armó alguien. */
function AccionCarrito({ carrito, onTomar }) {
  const navigate = useNavigate()
  if (carrito.nota_venta_tomada) {
    return (
      <div>
        <span style={st.linkNv} title="Abrir la nota de venta" onClick={() => navigate(`/notas-venta/${carrito.nota_venta_tomada.id}`)}>
          {carrito.nota_venta_tomada.numero_lgr}
        </span>
        <div style={st.subLinea}>tomado por {carrito.tomador?.nombre || 'un vendedor'} · {fmtFecha(carrito.tomado_at)} {fmtHoraDe(carrito.tomado_at)}</div>
      </div>
    )
  }
  return <Boton variante="primary" sm onClick={onTomar}>🛒 Tomar carrito</Boton>
}

function ModalTomar({ carrito, onClose }) {
  const navigate = useNavigate()
  const [cliente, setCliente] = useState(null)
  const [alta, setAlta] = useState(false)
  const [enviando, setEnviando] = useState(false)
  const [error, setError] = useState(null)

  const tomar = async () => {
    setEnviando(true); setError(null)
    try {
      const ruta = carrito.tipo === 'compartido' ? 'carritos-compartidos' : 'carritos-web'
      const { data } = await api.post(`/${ruta}/${carrito.id}/tomar`, { cliente_id: cliente.id })
      navigate(`/notas-venta/${data.nota_venta_id}`)
    } catch (e) {
      setError(e.response?.data?.errors?.carrito?.[0] || e.response?.data?.message || 'No se pudo tomar el carrito.')
      setEnviando(false)
    }
  }

  return (
    <Modal title={`Tomar el carrito ${carrito.codigo || ''}`.trim()} onClose={onClose} maxWidth="600px">
      <div style={st.lista}>
        {(carrito.lineas || []).map((l, i) => (
          <div key={i} style={st.renglon}>
            <span style={{ ...sx.mono, color: colors.textSec, minWidth: 36 }}>{l.cantidad}×</span>
            <span>{l.nombre || l.sku}</span>
          </div>
        ))}
        <div style={{ ...st.renglon, justifyContent: 'space-between', color: colors.textSec, borderTop: `1px solid ${colors.borderSoft}`, paddingTop: space.sm, marginTop: space.xs }}>
          <span>Subtotal sin promociones (el 6×5 lo aplica la nota)</span>
          <span style={sx.mono}>{carrito.total != null ? `$${fmtMonto(carrito.total)}` : '—'}</span>
        </div>
      </div>

      {carrito.motivo && <div style={st.error}>{carrito.motivo}</div>}
      {carrito.tipo === 'compartido' && (
        <div style={{ ...st.meta, marginBottom: space.md }}>Carrito compartido (ligier.com.ar/c/{carrito.codigo}): los precios son los de la tienda hoy.</div>
      )}

      <label style={st.label}>¿A nombre de quién va la venta?</label>
      {carrito.email && (
        <div style={{ ...st.meta, marginBottom: space.sm }}>El cliente estaba logueado como <b>{carrito.email}</b>: buscalo por ese email.</div>
      )}
      <div style={{ display: 'flex', gap: space.md, alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}><BuscadorCliente value={cliente} onChange={setCliente} placeholder="Buscar por nombre, CUIT, DNI o email..." /></div>
        <Boton variante="secondary" onClick={() => setAlta(true)}>+ Cliente nuevo</Boton>
      </div>

      <div style={{ ...st.meta, marginTop: space.lg }}>
        Se arma una nota de venta en borrador con la lista de la tienda (LISTA1) y sus precios, 6×5 incluido. La entrega y el envío los completás en la nota.
      </div>
      {error && <div style={st.error}>{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.md, marginTop: space.xl }}>
        <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
        <Boton variante="primary" disabled={!cliente || enviando} onClick={tomar}>{enviando ? 'Armando…' : '🛒 Tomar carrito y armar la NV'}</Boton>
      </div>

      {alta && <ModalAltaCliente onClose={() => setAlta(false)} onCreado={(c) => { setCliente(c); setAlta(false) }} />}
    </Modal>
  )
}

const st = {
  card: { ...sx.card, padding: `${space.lg}px ${space.xl}px`, marginBottom: space.xl },
  fila: { display: 'flex', alignItems: 'center', gap: space.lg, flexWrap: 'wrap' },
  titulo: { fontSize: text.body, color: colors.text },
  input: { flex: '1 1 320px', maxWidth: 480, padding: '6px 12px', fontSize: text.body, border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, fontFamily: font.sans, background: colors.bg, color: colors.text },
  ayuda: { fontSize: text.sec, color: colors.textSec, marginTop: space.sm, maxWidth: 760 },
  meta: { fontSize: text.sec, color: colors.textMeta },
  tabla: { ...sx.table, marginTop: space.md },
  th: sx.th,
  tr: { height: 44 },
  td: { ...sx.td, padding: `0 ${space.lg}px` },
  linkNv: { ...sx.mono, color: colors.link, cursor: 'pointer', fontWeight: 600 },
  subLinea: { fontSize: text.meta, color: colors.textMeta },
  lista: { fontSize: text.sec, color: colors.text, marginBottom: space.lg, display: 'flex', flexDirection: 'column', gap: 2 },
  renglon: { display: 'flex', gap: space.md },
  label: { display: 'block', fontSize: text.sec, fontWeight: 600, color: colors.text, marginBottom: space.sm },
  error: { background: colors.errBg, color: colors.err, borderRadius: radius.sm, padding: '8px 12px', marginTop: space.lg, fontSize: text.sec },
}
